import { nl } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";

import type { RiskPictureItem, RiskPictureLevel } from "./risk-picture-model";

const LEVEL_LABEL: Record<RiskPictureLevel, string> = {
  yellow: "geel",
  orange: "oranje",
  red: "rood",
};

export const riskPictureCellLabel = (
  item: Pick<RiskPictureItem, "kind" | "level" | "probabilityRange">,
): string => {
  if (item.kind === "issued") {
    return item.level ? `Code ${LEVEL_LABEL[item.level]}` : "Code";
  }

  return item.probabilityRange ? `Kans ${item.probabilityRange}%` : "Kans";
};

/** Times in the dashboard time zone, with Dutch weekdays only for different local dates. */
export const riskPicturePeriodLabel = (
  onset: string | undefined,
  expires: string | undefined,
  timeZone = "Europe/Amsterdam",
): string | undefined => {
  if (!onset || !expires) return undefined;
  const sameDay =
    formatInTimeZone(onset, timeZone, "yyyy-MM-dd") ===
    formatInTimeZone(expires, timeZone, "yyyy-MM-dd");
  const format = sameDay ? "HH:mm" : "EEEE HH:mm";
  const from = formatInTimeZone(onset, timeZone, format, { locale: nl });
  const to = formatInTimeZone(expires, timeZone, format, { locale: nl });
  return `Vanaf ${from} - tot ${to}`;
};

export const clampDayIndex = (index: number, dayCount: number): number => {
  if (dayCount <= 0) return 0;
  return Math.min(Math.max(index, 0), dayCount - 1);
};

export const itemsForPhenomenon = (
  items: RiskPictureItem[],
  phenomenon: string,
): RiskPictureItem[] => items.filter((item) => item.phenomenon === phenomenon);

/**
 * Lower bound, then upper bound. The wire buckets do not overlap, so 60-90
 * outranks 30-60. A missing or unparsed range loses to any real bucket.
 */
const chanceRangeRank = (range: string | undefined): number => {
  if (!range) return -1;
  const match = /^(\d+)-(\d+)$/.exec(range);
  if (!match) return -1;
  return Number(match[1]) * 1000 + Number(match[2]);
};

const tableItemKey = (
  item: Pick<RiskPictureItem, "phenomenon" | "kind" | "level">,
): string => `${item.phenomenon}\0${item.kind}\0${item.level ?? ""}`;

/** One item per phenomenon, kind and warning code in the table cell. First issued
 * warning wins; for chances the highest probability bucket wins. Group order is
 * the order of their first occurrence in the model.
 */
export const collapseTableItems = (items: RiskPictureItem[]): RiskPictureItem[] => {
  const chosen = new Map<string, RiskPictureItem>();
  for (const item of items) {
    const key = tableItemKey(item);
    const current = chosen.get(key);
    if (
      !current ||
      (item.kind === "chance" &&
        chanceRangeRank(item.probabilityRange) > chanceRangeRank(current.probabilityRange))
    ) {
      chosen.set(key, item);
    }
  }
  return [...chosen.values()];
};
